import type { PermissionKey } from '@kg-internal/pv3-helpers';
import {
  Admin,
  Calendar,
  Clipboard,
  Clock,
  Crosshairs,
  Handbook,
  Inventory,
  Learn,
  Phone,
  PowerHour,
  Rocket,
  Team,
  UserClock,
  Vault,
} from '../../../assets/icons/Icons';
import { holdsKey } from '../../../context/PortalContext/permissionChecks';
import { canViewPowerHours, getPowerHoursUrl } from '../../../utils/powerHours';

/**
 * THE PORTAL-WIDE APP SWITCHER, and the three things its map used to say with one string.
 *
 * The key of each entry used to be a legacy permission NAME, and it carried the permission to hold, the
 * item's identity for `selected`, and the map's ordering all at once. That is why this file was the last
 * place in the browser still reading `me { permissions }`: converting it needed the three to come apart
 * first. They are `id`, `requires` and insertion order now.
 *
 * `requires: null` is "everyone", and it is a DIFFERENT statement from a key nobody holds — the same
 * distinction `NO_PERMISSION_ARM` draws elsewhere. Six entries are null: five whose destination is
 * already open to every employee, so the legacy flag only hid the link, and Power Hours, whose gate is a
 * department and never was a permission. The disposition of each retired flag is recorded in the header
 * of 20261029-PRE-1-permissions.sql.
 *
 * THIS FILE WAS `.js`, WHICH IS HOW IT BROKE. `PortalContextMe` stopped selecting `permissions` and
 * `getVisibleNavItems` went on calling `.forEach` on it, so every app rendering <Navigation> threw on
 * first render and no typecheck saw a thing. It is TypeScript now so the next removal is a build error.
 */
type NavItem = {
  /** The granular key that reveals this entry, or `null` when the destination is open to everyone. */
  requires: PermissionKey | null;
  label: string;
  // The icons module is .jsx and exports untyped elements.
  // biome-ignore lint/suspicious/noExplicitAny: untyped icon module
  icon: any;
  href?: string;
  mobile: boolean;
  target?: string;
  rel?: string;
  display?: boolean;
  selected?: boolean;
};

/**
 * Only what this file reads off the user, declared structurally rather than as `IUserObj['details']`.
 * Importing that type reaches `userObj.ts`, which imports the app config, which touches `window` — and
 * this module is read by a jest suite under `server/graph`, whose tsconfig has no DOM lib. The real
 * `details` object is assignable to this by shape, and `null` is spelled out because it is: the `me`
 * query has not resolved on the first render.
 */
type NavUser = { permissionKeys?: readonly string[]; department?: { DepartmentID: number } | null } | null | undefined;

/**
 * Only the URLs this file reads, declared structurally rather than imported from `../../../config`.
 * That import is what a `.ts` file costs here: `config.ts` touches `window`, and this module is read by
 * a jest suite under `server/graph`, whose tsconfig has no DOM lib. An interface is assignable to this
 * by shape, so `config.urls` still passes without a cast.
 */
type NavUrls = {
  admin?: string;
  assetControl?: string;
  calendar?: string;
  chatKgpt?: string;
  directory?: string;
  handbook?: string;
  it?: string;
  licenseTracker?: string;
  myteam?: string;
  pto?: string;
  timetracker?: string;
  vault?: string;
};

// The Knowledge Center link by department, and the departments it is drawn for: Technology, Strategy and
// Design each have a Confluence space, and Digital lands on Confluence home.
const PLAYBOOKURL: Record<number, string> = {
  5: 'https://konradgroup.atlassian.net/wiki/spaces/CONSULTING/overview',
  6: 'https://konradgroup.atlassian.net/wiki/spaces/UXS/overview',
  4: 'https://konradgroup.atlassian.net/wiki/spaces/DEV/overview',
  19: 'https://konradgroup.atlassian.net/wiki/home',
};

/**
 * `details` is nullable on `IUserObj` and this module is called during the first render, before the `me`
 * query resolves. Every read of the key set goes through here so a logged-out or still-loading user gets
 * an empty list rather than a throw — which is the shape of the bug this file just had.
 *
 * An ARRAY rather than a Set, and each membership test goes through `holdsKey`, because that is the one
 * function the browser's platform-bypass switch is read by. A local Set with its own `.has` would put
 * the nav map outside the switch, so flipping it would move every other surface and not this one.
 */
const heldKeys = (user: NavUser): readonly string[] => user?.permissionKeys ?? [];

const getNavItems = (urls: NavUrls, user: NavUser): Record<string, NavItem> => {
  const departmentID = user?.department?.DepartmentID;
  const pburl = departmentID ? PLAYBOOKURL[departmentID] : undefined;

  const held = heldKeys(user);

  // Asset Control is ONE entry whose label and icon depend on the tier, where it used to be three
  // entries keyed by three legacy names. `app.assetcontrol` is entry to the tracker and admits nobody
  // `assetcontrol.admin` and `.lead` did not — 29 legacy holders either way — so it is the door, and
  // `asset.edit`, that service's admin surface, only chooses which of the two labels to draw.
  const isAssetControlAdmin = holdsKey(held, 'asset.edit');

  const appMappings: Record<string, NavItem> = {
    timetracker: {
      // `timesheet.view` is a BASELINE key, so this widens the entry from legacy `app.timetracker`'s 571
      // holders to every employee. Deliberate: the Project Tracker has no `PortalContext` door at all, so
      // the nav flag was the only thing hiding an app already open to everyone, and the 86 it hid it from
      // follow no rule — they span every joining year and every staff category.
      requires: 'timesheet.view',
      label: 'Project Tracker',
      icon: Clock,
      href: urls.timetracker,
      mobile: true,
    },
    // No permission gates the Directory app: it is available to everyone.
    //
    // THIS IS A WIDENING OF ONE PERSON, and an earlier version of this comment claimed otherwise — that
    // the entry was already unconditional. It was not. On `develop`, `getVisibleNavItems` sets
    // `display = true` for any nav item whose KEY matches a permission the user holds, and this entry's
    // key WAS `app.directory` with no hardcoded `display`, so the permission really did gate it.
    // Measured: 656 of 657 active people held it, and the one non-holder holds no permission of any
    // kind — an unprovisioned account rather than a deliberate exclusion.
    directory: {
      requires: 'app.directory',
      label: 'Directory',
      icon: Phone,
      href: urls.directory,
      mobile: true,
    },
    pto: {
      requires: 'app.pto',
      label: holdsKey(held, 'app.work_from_anywhere') ? 'PTO & Flexible Work' : 'PTO',
      icon: UserClock,
      href: urls.pto,
      mobile: true,
    },
    calendar: {
      requires: 'app.calendar',
      label: 'Events Calendar',
      icon: Calendar,
      href: urls.calendar,
      mobile: true,
    },
    // THE SERVICE DESK LINK IS GATED AGAIN, and it is worth being precise about what that buys: it points
    // at Jira, which is not ours, and anyone who types the URL reaches it with Atlassian deciding whether
    // they get in. So `app.it` is not a gate — it stops the portal advertising a destination the reader
    // has no account for, which is a real thing to want and not a security boundary (captain,
    // 2026-09-09). The Knowledge Center link is the same kind of thing, drawn by department below.
    //
    // `onekg` — "Vision" — is REMOVED rather than gated. It was a third link of the same kind, into the
    // handbook, and its legacy flag reached 2 of 657: gating a handbook link behind a permission 655
    // people lack advertises nothing and hides something everybody may read.
    handbook: {
      requires: 'app.handbook',
      label: 'Handbook',
      icon: Handbook,
      href: urls.handbook,
      mobile: true,
    },
    powerhours: {
      // Power Hours has no permission and never had one — visibility is department-only, applied in
      // getVisibleNavItems. `requires: null` states that the permission system is not the gate here.
      requires: null,
      label: 'Power Hours',
      icon: PowerHour,
      // External Confluence library, so open in a new tab.
      href: getPowerHoursUrl(departmentID),
      target: '_blank',
      rel: 'noopener noreferrer',
      mobile: true,
    },
    vault: {
      requires: 'app.vault',
      label: 'Vault',
      icon: Vault,
      href: urls.vault,
      mobile: true,
    },
    playbook: {
      requires: null,
      label: 'Knowledge Center',
      icon: Rocket,
      href: pburl,
      mobile: true,
    },
    assetcontrol: {
      requires: 'app.assetcontrol',
      label: isAssetControlAdmin ? 'Asset Control' : 'Asset Tracker',
      icon: isAssetControlAdmin ? Inventory : Clipboard,
      href: urls.assetControl,
      mobile: true,
    },
    myteam: {
      requires: 'app.myteam',
      label: 'My Team',
      icon: Team,
      href: urls.myteam,
      mobile: false,
    },
    licensetracker: {
      requires: 'app.licensetracker',
      label: 'License Tracker',
      icon: Crosshairs,
      href: urls.licenseTracker,
      mobile: true,
    },
    it: {
      requires: 'app.it',
      label: 'Service Desk',
      icon: Learn,
      href: urls.it,
      mobile: true,
    },
    /*chatkgpt: {
      requires: 'app.chatkgpt',
      label: 'Chat KGPT',
      icon: Comment,
      href: urls.chatKgpt,
      mobile: true,
    },*/
    admin: {
      requires: 'app.admin',
      label: 'Admin',
      icon: Admin,
      href: urls.admin,
      mobile: true,
    },
  };

  // Doing it this way for ordering purposes
  return appMappings;
};

/**
 * Gets the KG Portal nav items based on the current user permissions
 * @param urls urls for each KG Portal app
 * @param currentUser the current logged in user
 * @param selected the currently selected nav item
 */
const getPortalItems = (urls: NavUrls, currentUser: NavUser, selected?: string): NavItem[] => {
  const navItems = getNavItems(urls, currentUser);

  return getVisibleNavItems(navItems, currentUser, selected);
};

/**
 * Gets the visible navItems based on the user permissions
 * @param navItems navItems
 * @param currentUser the current logged in user
 * @param selected the currently selected nav item
 */
const getVisibleNavItems = (
  navItems: Record<string, NavItem>,
  currentUser: NavUser,
  selected?: string,
): NavItem[] => {
  const held = heldKeys(currentUser);

  for (const item of Object.values(navItems)) {
    item.display = item.requires === null || holdsKey(held, item.requires);
  }

  // Agent Admin opens the admin app on its own, whose only tab it then sees is Agents.
  if (navItems.admin && (holdsKey(held, 'agent_run.override') || holdsKey(held, 'agent_workflow_setting.edit'))) {
    navItems.admin.display = true;
  }

  // The Knowledge Center has no permission either: only the departments PLAYBOOKURL names get the link.
  if (navItems.playbook) {
    navItems.playbook.display = !!navItems.playbook.href;
  }

  // Power Hours has no permission — visibility is department-only: it shows only for
  // departments that have a Confluence library.
  if (navItems.powerhours) {
    navItems.powerhours.display = canViewPowerHours(currentUser?.department?.DepartmentID);
  }

  // The ids ARE the app short names callers pass, so this is a direct lookup. It used to be
  // `navItems['app.' + selected]` against ids spelled like legacy permissions, which highlighted the same
  // entries for the same callers — `asset-control` still misses `assetcontrol`, and `hiring`, `homepage`
  // and `bloodhound` have no entry in the map at all.
  //
  // `admin` DID start matching: its id was `portal.admin`, which no `app.`-prefixed lookup could ever
  // reach. That is a highlight appearing where one was always intended, not a new one.
  if (selected && navItems[selected]) {
    navItems[selected].selected = true;
  }

  return Object.values(navItems).filter((i) => i.display);
};

export { getNavItems, getPortalItems, getVisibleNavItems };
